import type { GroupedFilterOption } from '@models/Filter'
import type { GroupedOption } from '@models/GroupedOption'

import type { Project } from '../../aansluiting/project/types/Project'
import type { AuthenticatedUser } from '../../auth/types/AuthenticatedUser'
import type { SubprocesTemplate } from '../../entity/subprocessen/types/SubprocesTemplate'
import type { HoofdleidingProject } from '../../hoofdleiding/project/types/HoofdleidingProject'

import { alDisciplines, hlDisciplines } from './discipline'

export const getPossibleAssigneesForProject = ({
  project,
}: {
  project: Project | HoofdleidingProject
}) => {
  const { combi, coordAannemer, netbeheerdersContract, aannemers } = project
  const buildOption = (group) => (label) => ({ group, label })

  return [buildOption('Combi')(combi)]
    .concat(coordAannemer?.map(buildOption('Coordinerende')) || [])
    .concat(netbeheerdersContract?.map(buildOption('Netbeheerder')) || [])
    .concat(
      aannemers?.map(buildOption('Aannemer')) || [],
    ) as GroupedFilterOption[]
}

export const canCreateAssignedBijlageForProject = ({
  project,
  user,
  assignees,
}: {
  project: Project | HoofdleidingProject
  user: AuthenticatedUser | Record<string, any>
  assignees: GroupedOption[]
}): boolean => {
  const allowedStatusTextAssignedBijlage = [
    'Intake',
    'Werkvoorbereiding',
    'In uitvoering',
  ]
  const { type, statusText } = project
  const isHoofdleidingProject = type === 'Hoofdleiding'
  const isPossibleAssignee = assignees.some(
    (option: GroupedOption) => option.label === user.organisatie,
  )

  if (!user.canCreateAssignedBijlage) return false
  if (isHoofdleidingProject)
    return project.verantwoordelijken.includes(user.organisatie)

  return (
    isPossibleAssignee && allowedStatusTextAssignedBijlage.includes(statusText)
  )
}

export const canChangeProjectForProject = ({
  project,
  user,
}: {
  project: Project | HoofdleidingProject
  user: AuthenticatedUser | Record<string, any>
}): boolean => {
  const { type, verantwoordelijken } = project
  const isHoofdleidingProject = type === 'Hoofdleiding'

  return (
    isHoofdleidingProject &&
    !!user.canChangeProject &&
    verantwoordelijken.includes(user.organisatie)
  )
}

export const explodeKnownProjects = (
  linkedProjects: string[] | undefined, // can be undefined, so this is to ensure typing matches that
  accessProjectList: Project[],
) => {
  if (!linkedProjects || !linkedProjects.length) return []

  return linkedProjects.map(
    (id) =>
      accessProjectList.find((x) => x.aanvraagID === id) || { aanvraagID: id },
  )
}

export const isUserVerantwoordelijkForProject = ({
  user,
  project,
}: {
  user: AuthenticatedUser | Record<string, any>
  project: Project | HoofdleidingProject
}) => {
  return project.verantwoordelijken.includes(user.organisatie)
}

export const isUserAannemerForProject = ({
  user,
  project,
}: {
  user: AuthenticatedUser | Record<string, any>
  project: Project | HoofdleidingProject
}) => {
  return Boolean(
    project.aannemers?.includes(user.organisatie) ||
    project.coordAannemer?.includes(user.organisatie),
  )
}

export const canUserCreateSubprocesForProject = ({
  project,
  user,
  templates,
}: {
  project: Project | HoofdleidingProject
  user: AuthenticatedUser
  templates: SubprocesTemplate[]
}) => {
  const canCreateSchouw =
    user.canCreateSubprocesSchouw &&
    templates.some(
      (template) =>
        template.type === 'schouw' && template.projectType === project.type,
    )
  const canCreateBodem =
    user.canCreateSubprocesBodem &&
    templates.some(
      (template) =>
        template.type === 'bodem' && template.projectType === project.type,
    )
  const canCreateNetcheck =
    user.canCreateSubprocesNetcheck &&
    templates.some(
      (template) =>
        template.type === 'netcheck' && template.projectType === project.type,
    )

  return canCreateSchouw || canCreateBodem || canCreateNetcheck
}

export const updatableProps = [
  'gemeente',
  'naam',
  'typeWerk',
  'externeReferentie',
  'adres',
  'tracelengte',
  'woningen',
  'appartementen',
  'bedrijfsunits',
  'aanvraagDatum',
  'wensweek',
]

export const buildProjectDetailsUpdates = <T = Project | HoofdleidingProject>({
  baseProject = {},
  updatedProject,
}: {
  baseProject?: Partial<T>
  updatedProject: Partial<T>
}): Partial<T> => {
  return updatableProps.reduce((acc, key) => {
    if (baseProject[key] !== updatedProject[key]) {
      acc[key] = updatedProject[key]
    }

    return acc
  }, {})
}

const propLabelMap: Partial<Record<keyof HoofdleidingProject, string>> = {
  aanvraagDatum: 'Aanvraagdatum',
  adres: 'Adres',
  externeReferentie: 'Externe referentie',
  gemeente: 'Gemeente',
  naam: 'Naam',
  typeWerk: 'Type werk',
  tracelengte: 'Tracelengte',
  woningen: 'Woningen',
  appartementen: 'Appartementen',
  bedrijfsunits: 'Bedrijfsunits',
  wensweek: 'Wensweek',
}

/** Ondersteunt nog niet alle (hoofdleiding) project properties. Graag toevoegen
 * wanneer nodig.
 */
export const getProjectLabel = (
  key: keyof Project | keyof HoofdleidingProject,
): string => propLabelMap[key] || key

const makeOrganisatieInGroup =
  (organisatie: string) =>
  ({ code }: GroupedFilterOption) =>
    code === organisatie

export const filterVerantwoordelijkeOptionsByOrganisatie = (
  verantwoordelijken: GroupedFilterOption[],
  organisatie?: string,
): GroupedFilterOption[] => {
  if (!organisatie) return []

  const organisatieInGroup = makeOrganisatieInGroup(organisatie)

  const userGroups = verantwoordelijken.filter(organisatieInGroup)
  const combiGroups = verantwoordelijken.filter(
    ({ group }) => group === 'Combi',
  )
  const netbeheerderGroups = verantwoordelijken.filter(
    ({ group }) => group === 'Netbeheerder',
  )

  if (combiGroups.find(organisatieInGroup))
    return [...userGroups, ...netbeheerderGroups]

  if (netbeheerderGroups.find(organisatieInGroup))
    return [...combiGroups, ...userGroups]

  return verantwoordelijken // voor Beheerders (DSP) en Aannemers
}

export const allowedDisciplinesForProject = (
  project: Project | HoofdleidingProject,
) => {
  const allowedDisciplines =
    project.type === 'Hoofdleiding' ? hlDisciplines : alDisciplines

  return project.disciplineVerzamelingen.filter((dv) =>
    allowedDisciplines.includes(dv.disciplineCode),
  )
}

export function getProjectAdres(project: Project): string {
  if (!project) return ''

  if (project.adresAttributen) {
    const { straat, huisnummer, huisnummerToevoeging, postcode, plaats } =
      project.adresAttributen

    const nummer = huisnummerToevoeging
      ? `${huisnummer} ${huisnummerToevoeging}`
      : huisnummer

    return `${straat} ${nummer}\n${postcode} ${plaats}`
  }

  if (project.adres) {
    return project.adres
  }

  return ''
}

export function isGeschovenNaarAannemerAllowed(disciplineVerzameling: {
  netbeheerderLabel: string
}): boolean {
  return ['Stedin', 'Evides'].includes(disciplineVerzameling.netbeheerderLabel)
}

export function geschovenNaarAannemerWijzigbareDisciplines(
  { verantwoordelijken, disciplineVerzamelingen }: HoofdleidingProject,
  organisatie?: string,
) {
  const isVerantwoordelijke =
    !!organisatie && verantwoordelijken.includes(organisatie)

  return disciplineVerzamelingen
    .filter(isGeschovenNaarAannemerAllowed)
    .filter((dv) => isVerantwoordelijke || dv.netbeheerderCode === organisatie)
}
